import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import AdminLayout from '../components/AdminLayout';
import { FaPlus, FaEdit, FaTrash, FaSearch } from 'react-icons/fa';
import { apiService } from '../services/api';
import { normalizeListForAdmin, toBool } from '../utils/normalizeApi';

function FAQManagement() {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  
  const [formData, setFormData] = useState({
    question: '',
    answer: '',
    category: 'General',
    displayOrder: 1,
    isActive: true
  });

  useEffect(() => { fetchFAQs(); }, []);

  const fetchFAQs = async () => {
    try {
      const response = await apiService.faq.getAllAdmin();
      setFaqs(normalizeListForAdmin(response.data));
    } catch (error) {
      console.error('Error fetching FAQs:', error);
      setFaqs([]);
      alert('❌ Could not load FAQs: ' + (error.response?.data?.message || error.message));
    } finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.question.trim() || !formData.answer.trim()) {
      alert('Question and answer are required');
      return;
    }
    try {
      const payload = {
        question: formData.question.trim(),
        answer: formData.answer.trim(),
        category: formData.category,
        displayOrder: Number(formData.displayOrder) || 0,
        isActive: formData.isActive,
      };
      let res;
      if (editingId) {
        res = await apiService.faq.update(editingId, payload);
        if (res.data?.success === false) throw new Error(res.data?.message || 'Update failed');
        alert('✅ FAQ updated successfully!');
      } else {
        res = await apiService.faq.create(payload);
        if (res.data?.success === false) throw new Error(res.data?.message || 'Create failed');
        alert('✅ FAQ added successfully!');
      }
      setShowForm(false);
      setEditingId(null);
      resetForm();
      fetchFAQs();
    } catch (error) {
      console.error('CRUD Error:', error);
      alert('❌ Error: ' + (error.response?.data?.message || error.response?.data?.error || error.message));
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setFormData({
      question: item.question,
      answer: item.answer,
      category: item.category || 'General',
      displayOrder: item.displayOrder || 1,
      isActive: toBool(item.isActive, true)
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this FAQ?')) return;
    try {
      const res = await apiService.faq.delete(id);
      if (res.data?.success === false) throw new Error(res.data?.message || 'Delete failed');
      alert('✅ FAQ deleted successfully!');
      fetchFAQs();
    } catch (error) {
      console.error('Delete error:', error);
      alert('❌ Error deleting FAQ: ' + (error.response?.data?.message || error.message));
    }
  };

  const resetForm = () => {
    setFormData({ question: '', answer: '', category: 'General', displayOrder: 1, isActive: true });
  };

  if (loading) return <AdminLayout><div className="p-6">Loading...</div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="p-6">
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}
          className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-blue-900">FAQ Management</h1>
          <button onClick={() => setShowForm(true)}
            className="bg-blue-900 text-white px-6 py-3 rounded-lg hover:bg-blue-800 transition flex items-center gap-2">
            <FaPlus /> Add FAQ
          </button>
        </motion.div>

        {showForm && (
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
              <h2 className="text-2xl font-bold text-blue-900 mb-6">{editingId ? 'Edit' : 'Add'} FAQ</h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Question *</label>
                  <input type="text" value={formData.question} onChange={(e) => setFormData({ ...formData, question: e.target.value })} required
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-900" placeholder="Enter question" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Answer *</label>
                  <textarea value={formData.answer} onChange={(e) => setFormData({ ...formData, answer: e.target.value })} required rows="4"
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-900" placeholder="Enter answer" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                    <select value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-900">
                      <option value="General">General</option>
                      <option value="Admission">Admission</option>
                      <option value="Academic">Academic</option>
                      <option value="Transport">Transport</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Display Order</label>
                    <input type="number" value={formData.displayOrder} onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) })}
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-900" min="1" />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" checked={formData.isActive} onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="w-5 h-5 text-blue-900 rounded focus:ring-blue-900" />
                  <label className="text-sm text-gray-700">Active</label>
                </div>
                <div className="flex gap-4 pt-4">
                  <button type="submit" className="flex-1 bg-blue-900 text-white py-3 rounded-lg hover:bg-blue-800 transition font-semibold">
                    {editingId ? 'Update' : 'Add'} FAQ
                  </button>
                  <button type="button" onClick={() => { setShowForm(false); resetForm(); }}
                    className="flex-1 bg-gray-300 text-gray-700 py-3 rounded-lg hover:bg-gray-400 transition font-semibold">Cancel</button>
                </div>
              </form>
            </div>
          </motion.div>
        )}

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="space-y-4">
            {faqs.map((item) => (
              <div key={item.id} className="bg-white rounded-lg shadow p-6">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h3 className="font-bold text-lg text-blue-900 mb-2">{item.question}</h3>
                    <p className="text-gray-600">{item.answer}</p>
                    <div className="mt-2 flex gap-3 text-sm text-gray-500">
                      <span className="px-2 py-1 bg-gray-100 rounded">{item.category}</span>
                      <span>Order: {item.displayOrder}</span>
                    </div>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <button onClick={() => handleEdit(item)} className="text-blue-900 hover:text-blue-700"><FaEdit size={18} /></button>
                    <button onClick={() => handleDelete(item.id)} className="text-red-600 hover:text-red-800"><FaTrash size={18} /></button>
                  </div>
                </div>
                <div className="mt-3">
                  <span className={`px-2 py-1 rounded text-xs ${item.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {item.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            ))}
            {faqs.length === 0 && (
              <div className="text-center text-gray-600 py-12 bg-white rounded-lg shadow">
                <p className="text-xl">No FAQs yet</p>
                <p className="text-sm mt-2">Click &quot;Add FAQ&quot; to create one</p>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </AdminLayout>
  );
}

export default FAQManagement;
